/**
 * Utilidades para conversión de colores
 */

/**
 * Convierte un color hex a OKLCH usando el navegador
 * @param hex - Color en formato hex (ej: #FF5733 o FF5733)
 * @returns Color en formato OKLCH (ej: oklch(0.6292 0.2396 18.9856))
 */
export function hexToOklch(hex: string): string {
  // Normalizar el formato hex
  const normalizedHex = hex.startsWith('#') ? hex : `#${hex}`;
  
  // Si el color no es válido, retornar el color por defecto
  if (!/^#[0-9A-F]{6}$/i.test(normalizedHex)) {
    return 'oklch(0.64 0.24 14)'; // Color primario por defecto
  }

  try {
    // Usar el navegador para convertir hex a RGB
    const r = parseInt(normalizedHex.slice(1, 3), 16) / 255;
    const g = parseInt(normalizedHex.slice(3, 5), 16) / 255;
    const b = parseInt(normalizedHex.slice(5, 7), 16) / 255;
    
    // Convertir RGB a Linear RGB (sRGB to linear RGB)
    const linearR = r <= 0.04045 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
    const linearG = g <= 0.04045 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
    const linearB = b <= 0.04045 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
    
    // Convertir Linear RGB a OKLab (D65 white point)
    const l = 0.4122214708 * linearR + 0.5363325363 * linearG + 0.0514459929 * linearB;
    const m = 0.2119034982 * linearR + 0.6806995451 * linearG + 0.1073969566 * linearB;
    const s = 0.0883024619 * linearR + 0.2817188376 * linearG + 0.6299787005 * linearB;
    
    const l_ = Math.cbrt(l);
    const m_ = Math.cbrt(m);
    const s_ = Math.cbrt(s);
    
    const L = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_;
    const a = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
    const b_ = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;
    
    // Convertir OKLab a OKLCH
    const C = Math.sqrt(a * a + b_ * b_);
    let h = Math.atan2(b_, a) * (180 / Math.PI);
    if (h < 0) h += 360;
    
    // Formatear con precisión adecuada (mismo formato que en index.css)
    const LFormatted = L.toFixed(4);
    const CFormatted = C.toFixed(4);
    const hFormatted = h.toFixed(4);
    
    return `oklch(${LFormatted} ${CFormatted} ${hFormatted})`;
  } catch (error) {
    console.error('Error converting hex to OKLCH:', error);
    return 'oklch(0.64 0.24 14)'; // Color por defecto en caso de error
  }
}

/**
 * Convierte un color OKLCH a RGB usando el navegador
 * @param oklch - Color en formato OKLCH (ej: oklch(0.6292 0.2396 18.9856))
 * @returns Objeto con valores RGB { r, g, b } o null si hay error
 */
export function oklchToRgb(oklch: string): { r: number; g: number; b: number } | null {
  try {
    // Verificar que el DOM esté disponible
    if (typeof document === 'undefined' || !document.body) {
      return null;
    }
    
    // Crear un elemento temporal para usar el navegador para la conversión
    const tempElement = document.createElement('div');
    tempElement.style.color = oklch;
    tempElement.style.position = 'absolute';
    tempElement.style.visibility = 'hidden';
    tempElement.style.top = '-9999px';
    document.body.appendChild(tempElement);
    
    // Obtener el color computado en RGB
    const computedColor = window.getComputedStyle(tempElement).color;
    
    // Limpiar el elemento temporal
    document.body.removeChild(tempElement);
    
    // Extraer valores RGB del formato "rgb(r, g, b)"
    const rgbMatch = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      return {
        r: parseInt(rgbMatch[1], 10),
        g: parseInt(rgbMatch[2], 10),
        b: parseInt(rgbMatch[3], 10),
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error converting OKLCH to RGB:', error);
    return null;
  }
}

/**
 * Genera variaciones del color primario para otros tokens de color
 * @param primaryOklch - Color primario en formato OKLCH
 * @returns Objeto con variaciones del color
 */
export function generateColorVariations(primaryOklch: string) {
  // Extraer valores L, C, H del OKLCH
  const match = primaryOklch.match(/oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)\)/);
  if (!match) {
    // Valores por defecto si no se puede parsear
    const defaultRgb = { r: 232, g: 93, b: 63 };
    return {
      primary: primaryOklch,
      ring: primaryOklch,
      chart1: primaryOklch,
      sidebarPrimary: primaryOklch,
      sidebarRing: primaryOklch,
      primaryRgb: defaultRgb,
    };
  }
  
  const L = parseFloat(match[1]);
  const C = parseFloat(match[2]);
  const H = parseFloat(match[3]);
  
  // Convertir OKLCH a RGB
  const rgb = oklchToRgb(primaryOklch) || { r: 232, g: 93, b: 63 };
  
  // Generar variaciones manteniendo el mismo tono (H) pero ajustando luminosidad y croma
  return {
    primary: primaryOklch,
    ring: `oklch(${L.toFixed(4)} ${C.toFixed(4)} ${H.toFixed(4)})`,
    chart1: primaryOklch,
    sidebarPrimary: primaryOklch,
    sidebarRing: primaryOklch,
    primaryRgb: rgb,
  };
}

